import React, { FC, memo, useCallback } from 'react'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import {
  trackUIEvent,
  createProductViewedEvent,
} from '@moonpig/web-core-analytics'
import { Box } from '@moonpig/launchpad-components'
import {
  ProductClickedEvent,
  AddedToBasketEvent,
  Status,
  PromoTileData,
  calculateRowNumber,
  useNumberOfTilesPerRow,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { getDeviceType } from '@moonpig/web-shared-utils'
import { useStoreId } from '@moonpig/web-core-stores'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { ProductGridWithPaging } from '../ProductGridWithPaging'
import { SearchFiltersFacet } from '../../pages/GalleryPage/types'
import { useSetUIHCookie } from '../../utils'
import { useSearchStore } from '../../store/SearchStore'
import { SearchContext } from '../../store/slices/shared'
import { NoSearchResults } from '../NoSearchResults/NoSearchResults'
import { usePageContextFacets } from '../../store/helpers'

type ProductGridWithTrackingProps = {
  totalCount: number
  pageTitle: string
  metaTitle: string
  searchContext: SearchContext
  products: ProductTileProduct[]
  presetFacets: SearchFiltersFacet[]
  experiments?: { [key: string]: string } | undefined
  isInternalUser?: boolean
  onAddedToBasket: (e: AddedToBasketEvent) => void
  pageType: PageTypeEnum
  customerId?: string
  promoTile?: PromoTileData
}

export const ProductGridWithTrackingComponent: FC<
  ProductGridWithTrackingProps
> = ({
  totalCount,
  metaTitle,
  searchContext,
  products,
  experiments,
  onAddedToBasket,
  isInternalUser = false,
  pageType,
  customerId,
  promoTile,
}) => {
  const storeId = useStoreId()
  const { searchTerm, departments } = searchContext
  const { setUIHCookie } = useSetUIHCookie()
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const preAppliedFilters = useSearchStore(store => store.preAppliedFilters)
  const integerFilters = useSearchStore(store => store.integerFilters)
  const productsLoading = useSearchStore(store => store.loading)
  const noResultsFilters = useSearchStore(store => store.noResultsFilters)

  const pageContextFacets = usePageContextFacets()
  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux-v2', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const onProductClicked = useCallback(
    async ({ product }: ProductClickedEvent) => {
      setUIHCookie({
        product,
        facets: pageContextFacets,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setUIHCookie, selectedFilters, preAppliedFilters],
  )

  const { currentTilesPerRow } = useNumberOfTilesPerRow()

  const parseAppliedIntegerFilters = integerFilters.reduce<
    { name: string; min: number; max: number }[]
  >((acc, { integerId, value }) => {
    if (value == null) return acc

    acc.push({
      name: integerId,
      min: value,
      max: value,
    })

    return acc
  }, [])

  const onProductFirstClick = useCallback(
    async ({ product, index }: ProductClickedEvent) => {
      const { mnpg_ui_events_api_correlation_id: correlationId } =
        getBrowserCookies()
      if (process.env.API_URL) {
        try {
          await trackUIEvent(
            createProductViewedEvent({
              productId: product.id,
              searchTerm,
              department: departments,
              correlationId,
              filters: [
                ...selectedFilters.map(x => ({
                  facetKey: x.id,
                })),
                ...preAppliedFilters.map(x => ({
                  facetKey: x.key,
                })),
              ],
              integerFilters: parseAppliedIntegerFilters,
              store: storeId.toUpperCase(),
              ...(experiments && { metaData: { experiments } }),
              source: getDeviceType(),
              customerId,
              rowNumber: calculateRowNumber(
                (index ?? 0) + 1,
                currentTilesPerRow,
              ),
            }),
          )
        } catch (error) {
          // @eslint-disable-next-line @typescript-eslint/no-empty
        }
      }
    },
    [
      searchTerm,
      departments,
      selectedFilters,
      preAppliedFilters,
      storeId,
      experiments,
      customerId,
      currentTilesPerRow,
      parseAppliedIntegerFilters,
    ],
  )

  const onAddedToBasketWithTracking = React.useCallback(
    (e: AddedToBasketEvent) => {
      onAddedToBasket({
        ...e,
        componentName: 'product grid',
      })
    },
    [onAddedToBasket],
  )

  return (
    <Box>
      <Status
        loading={{
          status: productsLoading,
          fixed: true,
          zIndex: 999,
        }}
      />
      {!useAgeSelectionUx || noResultsFilters.length === 0 ? (
        <ProductGridWithPaging
          totalCount={totalCount}
          region={storeId}
          products={products}
          onProductClicked={onProductClicked}
          onProductFirstClick={onProductFirstClick}
          onAddToBasket={onAddedToBasketWithTracking}
          isInternalUser={isInternalUser}
          metaTitle={metaTitle}
          pageType={pageType}
          promoTile={promoTile}
        />
      ) : (
        <NoSearchResults noResultsFilters={noResultsFilters} />
      )}
    </Box>
  )
}

export const ProductGridWithTracking = memo(ProductGridWithTrackingComponent)
